import chroma from 'chroma-js';
import { BRIGHT_COLORS } from '~/constants/color';
import { calculateArea } from '~/helpers/unitHelpers';
import type { PolygonObject as MapPolygonObject } from '../../../../common/types';
import CanvasObject from '../CanvasObject';
import type SurfaceBase from '../SurfaceBase';
import type { ComputedPath, LngLat } from '../SurfaceBase';
import type VertexObject from '../VertexObject';

export default class PolygonObject extends CanvasObject {
  vertices: VertexObject[] = [];
  polygon: MapPolygonObject;

  firstCoordinate: LngLat;
  computedPath: ComputedPath;

  color: string;
  area;

  constructor(id: string, surface: SurfaceBase, polygon: MapPolygonObject) {
    super(id, surface);
    this.polygon = polygon;
    this.color = chroma(polygon.properties.color).alpha(polygon.properties.fillOpacity).hex();
    this.area = calculateArea({
      unitSystem: this.getStore().currentUnitSystem,
      geometry: polygon.geometry,
    });

    this.firstCoordinate = {
      lng: polygon.geometry.coordinates[0][0][0],
      lat: polygon.geometry.coordinates[0][0][1],
    };

    this.computedPath = surface.getPath(polygon.geometry.coordinates);
  }

  drawHitArea(ctx: OffscreenCanvasRenderingContext2D, color: string): void {
    const { properties } = this.polygon;
    const { x, y } = this.surface.toScreenCoords(this.firstCoordinate);
    const scale = 2 ** (this.surface.map.getZoom() - this.computedPath.z);
    const r = this.surface.mapBearing;

    const { width, height } = this.computedPath.bounds;
    const maxWidth = Math.max(width, height);

    // if the total lineWidth is greater than the width/height of the object
    // then don't render the object at all, will be too far zoomed out.
    if (properties.strokeWidth / scale > maxWidth) {
      return;
    }

    ctx.save();
    ctx.strokeStyle = color;
    ctx.fillStyle = color;
    ctx.lineWidth = 3 * (properties.strokeWidth / scale);
    ctx.translate(x, y);
    ctx.scale(scale, scale);
    ctx.rotate(r);
    ctx.stroke(this.computedPath.path);
    ctx.fill(this.computedPath.path);
    ctx.restore();
  }

  render(ctx: CanvasRenderingContext2D): void {
    const { properties } = this.polygon;
    const r = this.surface.mapBearing;
    const { x, y } = this.surface.toScreenCoords(this.firstCoordinate);
    const scale = 2 ** (this.surface.map.getZoom() - this.computedPath.z);
    const { width, height } = this.computedPath.bounds;
    const maxWidth = Math.max(width, height);

    // if the total lineWidth is greater than the width/height of the object
    // then don't render the object at all, will be too far zoomed out.
    if (properties.strokeWidth / scale > maxWidth) {
      return;
    }

    // render polygon path
    ctx.save();
    ctx.globalAlpha = properties.strokeOpacity;
    ctx.strokeStyle = properties.color;
    ctx.fillStyle = this.color;
    ctx.lineWidth = properties.strokeWidth / scale;
    properties.strokeStyle === 'dashed' && ctx.setLineDash([10, 4]);

    // translate to firstPoint, scale accordingly, and rotate the shape based on mapBearing;
    ctx.translate(x, y);
    ctx.scale(scale, scale);
    ctx.rotate(r);

    ctx.beginPath();
    ctx.stroke(this.computedPath.path);
    ctx.globalAlpha = 1;
    ctx.fill(this.computedPath.path);
    ctx.restore();

    // render polygon label
    const a = this.area;
    const padding = 12 / scale;
    const fontSize = 16 / scale;
    const textLines: string[] = [];

    // NOTE: Center is computed based off path vertices which are zero-based;
    const c = this.computedPath.center;

    ctx.save();
    ctx.font = `bold ${fontSize}px Inter`;
    // translate to first point and scale accordingly;
    ctx.translate(x, y);
    ctx.scale(scale, scale);

    // rotate map to match bearing (same as above), then translate the rest of the way
    // to the center coordinate, and rotate back to zero so text is not rotated.
    ctx.rotate(r);
    ctx.translate(c.x, c.y);
    ctx.rotate(-r);

    if (properties.showTitle && this.polygon.title) {
      textLines.push(this.polygon.title);
    }

    if (properties.showArea) {
      textLines.push(`${a.value.toLocaleString()} ${a.unit}`);
    }

    // if no text, restore and return;
    if (textLines.length === 0) {
      ctx.restore();
      return;
    }

    const textHeight = fontSize * textLines.length;
    let textWidth: number = 0;

    for (let i = 0; i < textLines.length; i++) {
      const t = textLines[i];
      const w = ctx.measureText(t).width;
      textWidth = Math.max(textWidth, w);
    }

    const pw = this.computedPath.bounds.width;

    // Only show label if the label is always visible
    // or if the label width is less than polygon width;
    if (!properties.labelAlwaysVisible && textWidth > pw) {
      ctx.restore();
      return;
    }

    // NOTE: because of the context translation above, we are at coords (0,0) here,
    // hence no need to place the label at (c.y, c.y);
    ctx.fillStyle = properties.color;
    ctx.beginPath();
    ctx.roundRect(
      -textWidth / 2 - padding / 2,
      -textHeight / 2 - padding / 2,
      textWidth + padding,
      textHeight + padding + (textLines.length - 1) * padding,
      [5 / scale],
    );
    ctx.fill();

    ctx.textBaseline = 'top';
    ctx.textAlign = 'center';
    // ctx.fillStyle = 'white';
    ctx.fillStyle = BRIGHT_COLORS.includes(properties.color) ? '#000000' : '#FFFFFF';
    const tx = 0;
    const ty = -textHeight / 2;

    for (let i = 0; i < textLines.length; i++) {
      ctx.fillText(textLines[i], tx, ty + i * fontSize + (i !== 0 ? padding : 0));
    }

    ctx.restore();
  }
}
